import { Children, type ReactElement, type ReactNode } from 'react';
import { s, type SpacingToken } from '@/theme/tokens';
import { Box, Stack, Surface, Text } from '@/ui';
import { Inline } from '@/ui/layout/Inline';
import type { SxProps, Theme } from '@/ui/primitives/types';

export type FormCardProps = {
  title?: string;
  titleIcon?: ReactNode;
  titleSlot?: ReactNode;
  titleSx?: SxProps<Theme>;
  description?: ReactNode;
  topSlot?: ReactNode;
  headerSlot?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  padding?: SpacingToken | null;
  gap?: SpacingToken;
  contentGap?: SpacingToken;
  headerGap?: SpacingToken;
  footerGap?: SpacingToken;
  container?: 'surface' | 'none';
  border?: boolean;
  surfaceVariant?: 'default' | 'settings';
  sx?: SxProps<Theme>;
};

export const FormCard = ({
  title,
  titleIcon,
  titleSlot,
  titleSx,
  description,
  topSlot,
  headerSlot,
  footer,
  children,
  padding,
  gap = 'md',
  contentGap = 'md',
  headerGap = 'sm',
  footerGap = 'md',
  container = 'surface',
  border = true,
  surfaceVariant = 'default',
  sx,
}: FormCardProps): ReactElement => {
  const resolvedPadding = padding ?? (surfaceVariant === 'settings' ? 'xl' : 'lg');
  const baseTitleSx: SxProps<Theme> =
    surfaceVariant === 'settings'
      ? {
          pl: s('none'),
          fontSize: '19px',
          lineHeight: 1.3,
        }
      : { pl: s('none') };
  const resolvedTitleSx = titleSx
    ? ([baseTitleSx, ...(Array.isArray(titleSx) ? titleSx : [titleSx])] as SxProps<Theme>)
    : baseTitleSx;
  const hasHeader = Boolean(title ?? description ?? headerSlot);
  const hasContent = children !== null && children !== undefined && children !== false;
  const resolvedTopSlot = Children.toArray(topSlot);
  const resolvedHeaderSlot = Children.toArray(headerSlot);
  const resolvedChildren = Children.toArray(children);
  const resolvedFooter = Children.toArray(footer);
  const titleContent = title ? (
    <Inline align="center" gap="sm" sx={{ flex: '1 1 auto', width: 'auto' }}>
      {titleIcon ? (
        <Box
          sx={{
            alignItems: 'center',
            color: 'common.white',
            display: 'inline-flex',
            flex: '0 0 auto',
            '& .MuiSvgIcon-root': {
              fontSize: (theme) => theme.app.iconSizes.md,
            },
          }}
        >
          {titleIcon}
        </Box>
      ) : null}
      <Text role="sectionTitle" sx={resolvedTitleSx}>
        {title}
      </Text>
    </Inline>
  ) : null;

  const content = (
    <Stack gap={gap}>
      {resolvedTopSlot.length > 0 ? <Box>{resolvedTopSlot}</Box> : null}
      {hasHeader ? (
        <Stack gap={headerGap}>
          {title ? (
            titleSlot ? (
              <Inline justify="space-between" align="center">
                {titleContent}
                <Box>{titleSlot}</Box>
              </Inline>
            ) : (
              titleContent
            )
          ) : null}
          {description ? (
            typeof description === 'string' ? (
              <Text
                role="body"
                color="textSecondary"
                sx={{
                  fontSize: (theme) => theme.typography.subtitle1.fontSize,
                  lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
                }}
              >
                {description}
              </Text>
            ) : (
              description
            )
          ) : null}
          {resolvedHeaderSlot.length > 0 ? <Box>{resolvedHeaderSlot}</Box> : null}
        </Stack>
      ) : null}
      {hasContent ? <Stack gap={contentGap}>{resolvedChildren}</Stack> : null}
      {resolvedFooter.length > 0 ? (
        <Box
          sx={{
            mt: s(footerGap),
            fontSize: (theme) => theme.typography.subtitle1.fontSize,
            lineHeight: (theme) => theme.typography.subtitle1.lineHeight,
          }}
        >
          {resolvedFooter}
        </Box>
      ) : null}
    </Stack>
  );

  if (container === 'none') {
    const resolvedBaseSx = {
      p: s(resolvedPadding),
      backgroundColor: 'background.paper',
      backgroundImage: 'none',
      opacity: 1,
      isolation: 'isolate',
    } satisfies SxProps<Theme>;
    const resolvedSx = sx
      ? ([resolvedBaseSx, ...(Array.isArray(sx) ? sx : [sx])].filter(Boolean) as SxProps<Theme>)
      : resolvedBaseSx;
    return <Box sx={resolvedSx}>{content}</Box>;
  }

  return (
    <Surface
      padding={resolvedPadding}
      border={border}
      sx={
        sx
          ? ([
              { backgroundColor: 'background.paper', backgroundImage: 'none', opacity: 1 },
              sx,
            ] as SxProps<Theme>)
          : ([
              { backgroundColor: 'background.paper', backgroundImage: 'none', opacity: 1 },
            ] as SxProps<Theme>)
      }
    >
      {content}
    </Surface>
  );
};
